
import { createContext, useState, useEffect, ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;               // 認証状態が確定しているか
  isAdmin: boolean;               // 管理者かどうか
  isAdminLoading: boolean;        // 管理者判定中か
  authError: string | null;
  signIn: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  signUp: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  signOut: () => Promise<void>;
  hardResetAuth: () => Promise<void>;
}

const defaultAuthContextValue: AuthContextType = {
  user: null,
  session: null,
  loading: true,
  isAdmin: false,
  isAdminLoading: false,
  authError: null,
  signIn: async () => ({ ok: false }),
  signUp: async () => ({ ok: false }),
  signOut: async () => {},
  hardResetAuth: async () => {},
};

export const AuthContext = createContext<AuthContextType>(defaultAuthContextValue);

type AuthProviderProps = {
  children: ReactNode;
};

const AUTH_INIT_TIMEOUT_MS = 8000;
const AUTO_LOGIN_FLAG_KEY = 'kyo-colle:demo-admin-autologin-done';
const DEMO_ADMIN_EMAIL = import.meta.env.VITE_DEMO_ADMIN_EMAIL || 'admin@zion-craft.work';
const DEMO_ADMIN_PASSWORD = import.meta.env.VITE_DEMO_ADMIN_PASSWORD || 'admin123';

// ----------------------------------------------------
// Refresh Token エラー検知関数
// ----------------------------------------------------
const isRefreshTokenError = (error: any): boolean => {
  if (!error) return false;
  const msg = error.message || '';
  return (
    msg.includes('Invalid Refresh Token') ||
    msg.includes('Refresh Token Not Found') ||
    msg.includes('refresh_token_not_found')
  );
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);      // 認証状態のローディング
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminLoading, setIsAdminLoading] = useState(false); // 管理者判定ローディング
  const [authError, setAuthError] = useState<string | null>(null);

  // ----------------------------------------------------
  // 安全なセッションリセット（Refresh Token エラー時）
  // ----------------------------------------------------
  const resetAuthSession = async (reason: string) => {
    console.warn(`🔄 [Auth] セッションリセット: ${reason}`);
    
    try {
      // 1. Supabase のセッションを終了
      await supabase.auth.signOut({ scope: 'local' });
    } catch (e) {
      console.warn('⚠️ [Auth] signOut 失敗（無視）', e);
    }

    // 2. localStorage の sb-* キーを削除
    try {
      Object.keys(window.localStorage)
        .filter((key) => key.startsWith('sb-'))
        .forEach((key) => window.localStorage.removeItem(key));
    } catch (e) {
      console.warn('⚠️ [Auth] localStorage cleanup 失敗（無視）', e);
    }

    // 3. 状態をリセット
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setIsAdminLoading(false);
    setAuthError(null);
    setAuthLoading(false);

    // 4. ログインページへ遷移
    console.log('🔄 [Auth] /login へリダイレクト');
    window.location.assign('/login');
  };

  // ----------------------------------------------------
  // 管理者判定
  // ----------------------------------------------------
  const checkAdminStatus = async (currentUser: User): Promise<void> => {
    try {
      console.log('🔐 [Auth] 管理者判定を開始します。ユーザーID:', currentUser.id);
      setIsAdminLoading(true);

      const { data, error } = await supabase
        .from('admin_users')
        .select('id')
        .eq('user_id', currentUser.id)
        .single();

      if (error) {
        if (error.code !== 'PGRST116') {
          console.error('❌ [Auth] 管理者チェックエラー:', error.message);
        } else {
          console.log('🔐 [Auth] 管理者レコードが見つかりません（一般ユーザー）');
        }
        setIsAdmin(false);
        setIsAdminLoading(false);
        return;
      }

      const adminStatus = !!data;
      setIsAdmin(adminStatus);
      setIsAdminLoading(false);
      console.log('👤 [Auth] 管理者ステータス:', adminStatus ? '管理者' : '一般ユーザー');
    } catch (error) {
      console.error('❌ [Auth] 管理者チェック例外:', error);
      setIsAdmin(false);
      setIsAdminLoading(false);
    }
  };

  // ----------------------------------------------------
  // デモ管理者アカウントへの自動ログイン（1回だけ実行）
  // ----------------------------------------------------
  const tryAutoLogin = async (): Promise<boolean> => {
    try {
      if (localStorage.getItem(AUTO_LOGIN_FLAG_KEY) === '1') {
        console.log('🔐 [Auth] 自動ログインは既に実行済みです');
        return false;
      }

      console.log('🔐 [Auth] デモ管理者アカウントへの自動ログインを試行します');
      localStorage.setItem(AUTO_LOGIN_FLAG_KEY, '1');

      const { data, error } = await supabase.auth.signInWithPassword({
        email: DEMO_ADMIN_EMAIL,
        password: DEMO_ADMIN_PASSWORD,
      });

      if (error) {
        console.error('❌ [Auth] 自動ログイン失敗:', error.message);
        localStorage.removeItem(AUTO_LOGIN_FLAG_KEY);
        return false;
      }

      if (data.session) {
        console.log('✅ [Auth] 自動ログイン成功');
        return true;
      }
      return false;
    } catch (e) {
      console.error('❌ [Auth] 自動ログイン例外:', e);
      localStorage.removeItem(AUTO_LOGIN_FLAG_KEY);
      return false;
    }
  };

  // ----------------------------------------------------
  // 明示的なログアウト時だけ実行するハードリセット
  // ----------------------------------------------------
  const hardResetAuth = async () => {
    console.log('🔄 [Auth] hardResetAuth: start');
    try {
      Object.keys(window.localStorage)
        .filter((key) => key.startsWith('sb-'))
        .forEach((key) => window.localStorage.removeItem(key));
      console.log('✅ [Auth] cleared local sb-* keys');
    } catch (e) {
      console.error('⚠️ [Auth] localStorage cleanup error (ignored)', e);
    }

    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setIsAdminLoading(false);
    setAuthError(null);
    setAuthLoading(false);
    console.log('✅ [Auth] hardResetAuth: completed');
  };

  // ----------------------------------------------------
  // 初期化ロジック
  // ----------------------------------------------------
  useEffect(() => {
    let isMounted = true;
    let timeoutId: number | undefined;
    let subscription: { unsubscribe: () => void } | undefined;

    const initAuth = async () => {
      console.log('🔐 [Auth] initAuth: start');
      setAuthLoading(true);
      setAuthError(null);

      // タイムアウト時は警告だけ（hardResetAuth は呼ばない）
      timeoutId = window.setTimeout(() => {
        if (!isMounted) return;
        console.warn('⏱️ [Auth] 初期化に時間がかかっています（タイムアウト）');
        setAuthError('ログイン状態の確認に時間がかかっています。');
      }, AUTH_INIT_TIMEOUT_MS);

      try {
        // ① onAuthStateChange のリスナー設定
        console.log('🔐 [Auth] onAuthStateChange のリスナーを設定します');
        const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
          async (event, currentSession) => {
            console.log('🔄 [Auth] onAuthStateChange イベント:', event, {
              hasSession: !!currentSession,
              hasUser: !!currentSession?.user,
            });

            if (!isMounted) return;

            // TOKEN_REFRESHED イベントでセッションがない場合はリセット
            if (event === 'TOKEN_REFRESHED' && !currentSession) {
              console.warn('⚠️ [Auth] TOKEN_REFRESHED だがセッションなし → リセット');
              await resetAuthSession('TOKEN_REFRESHED でセッション取得失敗');
              return;
            }

            // SIGNED_OUT → 明示的にログアウトしたときだけ状態リセット
            if (event === 'SIGNED_OUT') {
              console.log('🚪 [Auth] SIGNED_OUT イベント検知、状態リセット');
              setUser(null);
              setSession(null);
              setIsAdmin(false);
              setIsAdminLoading(false);
              setAuthError(null);
              setAuthLoading(false);
              // ホームへ遷移は onAuthStateChange 後に行う
              if (typeof window !== 'undefined' && (window as any).REACT_APP_NAVIGATE) {
                (window as any).REACT_APP_NAVIGATE('/');
              }
              return;
            }

            // SIGNED_IN / INITIAL_SESSION / TOKEN_REFRESHED → セッション更新
            if (['SIGNED_IN', 'INITIAL_SESSION', 'TOKEN_REFRESHED'].includes(event)) {
              const currentUser = currentSession?.user ?? null;
              setSession(currentSession);
              setUser(currentUser);
              setAuthLoading(false);

              if (currentUser) {
                await checkAdminStatus(currentUser);
              } else {
                setIsAdmin(false);
                setIsAdminLoading(false);
              }
              return;
            }

            // その他のイベント（例: USER_UPDATED）
            const currentUser = currentSession?.user ?? null;
            setSession(currentSession);
            setUser(currentUser);
            if (currentUser) {
              await checkAdminStatus(currentUser);
            } else {
              setIsAdmin(false);
              setIsAdminLoading(false);
            }
          },
        );
        subscription = authSubscription;

        // ② 現在のセッション取得
        console.log('🔐 [Auth] getSession を呼び出します');
        const { data, error } = await supabase.auth.getSession();

        if (!isMounted) return;

        // タイムアウト解除
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
          timeoutId = undefined;
        }

        // Refresh Token エラーの検知
        if (error && isRefreshTokenError(error)) {
          await resetAuthSession('getSession で refresh token エラー検知');
          return;
        }

        if (error) {
          console.error('❌ [Auth] getSession error:', error);
          setAuthError('ログイン情報の取得に失敗しました。');
          setAuthLoading(false);
          return;
        }

        const currentSession = data?.session ?? null;
        if (currentSession) {
          const currentUser = currentSession.user ?? null;
          setSession(currentSession);
          setUser(currentUser);
          setAuthLoading(false);
          if (currentUser) {
            await checkAdminStatus(currentUser);
          } else {
            setIsAdminLoading(false);
          }
        } else {
          // セッションが無い → 開発環境のみ自動ログインの試行
          if (import.meta.env.DEV) {
            console.log('🔐 [Auth] no active session, trying auto-login (DEV only)');
            const autoLoginSuccess = await tryAutoLogin();

            if (!autoLoginSuccess) {
              console.log('🔐 [Auth] 未ログイン状態として確定');
              setUser(null);
              setSession(null);
              setIsAdmin(false);
              setIsAdminLoading(false);
              setAuthLoading(false);
            }
            // 成功時は onAuthStateChange の SIGNED_IN で処理が走ります
          } else {
            // 本番環境では自動ログインをスキップ
            console.log('🔐 [Auth] no active session (production mode)');
            setUser(null);
            setSession(null);
            setIsAdmin(false);
            setIsAdminLoading(false);
            setAuthLoading(false);
          }
        }
      } catch (e) {
        if (!isMounted) return;
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
          timeoutId = undefined;
        }
        
        // 予期しないエラーでも Refresh Token エラーを検知
        if (isRefreshTokenError(e)) {
          await resetAuthSession('getSession で予期しない refresh token エラー');
          return;
        }
        
        console.error('❌ [Auth] initAuth unexpected error:', e);
        setAuthError('ログイン状態の確認中にエラーが発生しました。');
        setAuthLoading(false);
      }
    };

    void initAuth();

    // クリーンアップ
    return () => {
      console.log('🔐 [Auth] クリーンアップ: リスナー解除');
      isMounted = false;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      if (subscription) subscription.unsubscribe();
    };
  }, []);

  // ----------------------------------------------------
  // サインイン
  // ----------------------------------------------------
  const signIn = async (email: string, password: string) => {
    try {
      console.log('🔐 [Auth] ログイン試行中...', { email });
      setAuthError(null);
      setAuthLoading(true);

      const { error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        console.error('❌ [Auth] ログインエラー:', error.message);
        setAuthError(error.message);
        setAuthLoading(false);
        return { ok: false, error: error.message };
      }

      // セッション再取得で state を同期
      const { data: sessionData, error: sessErr } = await supabase.auth.getSession();
      if (sessErr) {
        console.error('⚠️ [Auth] getSession after signIn error', sessErr);
        setAuthLoading(false);
        return { ok: false, error: sessErr.message };
      }

      if (sessionData?.session) {
        const curUser = sessionData.session.user ?? null;
        setSession(sessionData.session);
        setUser(curUser);
        setAuthLoading(false);
        if (curUser) await checkAdminStatus(curUser);
      } else {
        console.warn('⚠️ [Auth] セッションが取得できませんでした');
        setAuthLoading(false);
      }

      console.log('✅ [Auth] ログイン成功');
      return { ok: true };
    } catch (e) {
      console.error('❌ [Auth] ログイン例外:', e);
      setAuthError('ログイン時にエラーが発生しました。');
      setAuthLoading(false);
      return { ok: false, error: e instanceof Error ? e.message : 'ログインに失敗しました' };
    }
  };

  // ----------------------------------------------------
  // サインアップ
  // ----------------------------------------------------
  const signUp = async (email: string, password: string) => {
    try {
      console.log('📝 [Auth] サインアップ試行中...', { email });
      setAuthError(null);
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) {
        console.error('❌ [Auth] サインアップエラー:', error.message);
        return { ok: false, error: error.message };
      }
      console.log('✅ [Auth] サインアップ成功');
      return { ok: true };
    } catch (e) {
      console.error('❌ [Auth] サインアップ例外:', e);
      return { ok: false, error: 'サインアップに失敗しました。' };
    }
  };

  // ----------------------------------------------------
  // ログアウト（明示的に呼ばれたときだけ hardResetAuth）
  // ----------------------------------------------------
  const signOut = async () => {
    try {
      console.log('🚪 [Auth] ログアウト試行中...');
      
      // ✅ 1. 先に Supabase 側のセッションを終了
      await supabase.auth.signOut();
      console.log('✅ [Auth] supabase.auth.signOut 完了');
      
      // ✅ 2. その後でローカルの sb-* キーなどをクリア
      await hardResetAuth();
      console.log('✅ [Auth] ログアウト完了');
      // onAuthStateChange の SIGNED_OUT で UI リセットが走ります
    } catch (e) {
      console.error('❌ [Auth] ログアウトエラー:', e);
      await hardResetAuth(); // 失敗時でもローカル状態はリセット
      throw e;
    }
  };

  const value = {
    user,
    session,
    loading: authLoading,          // 認証状態のローディングだけ
    isAdmin,
    isAdminLoading,
    authError,
    signIn,
    signUp,
    signOut,
    hardResetAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
